import {
  CameraAltOutlined as CameraIcon,
  DeleteOutlined as DeleteIcon,
  FileUploadOutlined as FileUploadIcon,
  CameraAlt as CameraAltIcon,
  Cameraswitch as CameraswitchIcon
} from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Modal, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { AlertBanner, SearchInput } from "../../components/FormComponents";
import { useLoginAction } from "../../hooks/useLoginAction";
import { getValidationErrors, useLoginStore } from "../../store/useLoginStore";
import { useSystemStateStore } from "../../store/useSystemStateStore";
import { EdgeSystemStatus, EdgeSystemType, EdgeUserType } from "../../types";
import { getEndpointConfiguration, getInitialLoginData } from "../../utils";
import { LOGIN_PAGE_NS } from "./i18n";
import { Id, Otp } from "./Icons";
import { LoginFormField } from "./LoginFormField";
import LoginOTPInput from "./LoginOTPInput";
import { LoginPageWrapper } from "./LoginPageWrapper";
import { RequestOTP } from "./RequestOTP/RequestOTP";
import { useWebcam } from '../../hooks/useWebcam';

enum LoginType {
  Otp = "Otp",
  Upload = "Upload"
};

const LoginOptionSwitcher = (props: {
  loginType: LoginType,
  setLoginType: (loginType: LoginType) => void
}) => {
  const isOtpLogin = props.loginType === LoginType.Otp;
  const isUploadLogin = props.loginType === LoginType.Upload;

  return (
    <Box sx={{ 
      display: 'flex', 
      mb: 2, 
      width: '460px',
      height: '36px',
      backgroundColor: '#F8F8F8',
      borderRadius: '6px',
      border: '1px solid rgba(232, 234, 235, 1)',
      overflow: 'hidden',
      padding: '0',
      gap: '0',
      position: 'relative',
    }}>
      <Button
        variant={isOtpLogin ? 'contained' : 'text'}
        onClick={() => props.setLoginType(LoginType.Otp)}
        startIcon={<Otp color={isOtpLogin ? '#1D82C0' : '#989B9C'} />}
        sx={{ 
          flex: 1, 
          height: '36px',
          minHeight: '36px',
          backgroundColor: 'transparent',
          color: isOtpLogin ? '#0C0C0C' : '#65686B',
          fontFamily: 'Noto Sans, sans-serif',
          fontWeight: isOtpLogin ? 700 : 500,
          textTransform: 'none',
          borderRadius: '6px',
          padding: '0',
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        One time password
      </Button>

      <Button
        variant={isUploadLogin ? 'contained' : 'text'}
        onClick={() => props.setLoginType(LoginType.Upload)}
        startIcon={<Id color={isUploadLogin ? '#1D82C0' : '#989B9C'} />}
        sx={{ 
          flex: 1,
          height: '36px',
          minHeight: '36px',
          backgroundColor: 'transparent',
          color: isUploadLogin ? '#0C0C0C' : '#65686B',
          fontFamily: 'Noto Sans, sans-serif',
          fontWeight: isUploadLogin ? 700 : 500,
          textTransform: 'none',
          borderRadius: '6px',
          padding: '0',
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        Upload ID
      </Button>

      <Box
        sx={{
          position: 'absolute',
          left: isOtpLogin ? '0' : '50%',
          width: '50%',
          height: '100%',
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          transition: 'left 0.3s ease',
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.05)',
        }}
      />
    </Box>
  );
};

export const LoginForm = () => {
  const { t } = useTranslation(LOGIN_PAGE_NS);
  const { palette } = useTheme();
  const {
    systemState: {
      [EdgeSystemType.CloudConnection]: { status: cloudConnectionStatus }
    }
  } = useSystemStateStore();
  const { doLogin } = useLoginAction();
  const { search: urlSearchQuery } = useLocation();
  const { isCloud } = getEndpointConfiguration();

  const {
    loginHasFailed,
    isSubmitButtonLoading,
    loginData,
    setLoginHasFailed,
    updateLoginData,
    setValidationErrors
  } = useLoginStore();

  const [loginType, setLoginType] = useState(LoginType.Otp);
  const [showRequestTokenForm, setShowRequestTokenForm] = useState(false);
  const [isOfflineLoginClicked, setIsOfflineLoginClicked] = useState(false);

  const [drag, setDrag] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const currentValidationErrors = useMemo(() => getValidationErrors(loginData), [loginData]);

  const handleFileUpload = useCallback((file: File) => {
    setUploadedFile(file);
  }, []);

  const { onWebcamClick, switchCamera, showCamera, handleCloseCamera, handleTakePhoto, error } = useWebcam({
    handleFileUpload,
    videoRef
  });

  const userType = loginData.userType;
  const isBargeLogin = userType === EdgeUserType.Barge;

  const cloudWasNotConnected = useRef<boolean>(false);  
  const cloudIsDisconnected = cloudConnectionStatus === EdgeSystemStatus.Error;
  const cloudIsReconnectingAfterError = cloudWasNotConnected.current && cloudConnectionStatus === EdgeSystemStatus.Ongoing;
  const cloudIsNotConnected = cloudIsDisconnected || cloudIsReconnectingAfterError;
  
  const showOtplessLoginLink = !isBargeLogin && cloudIsNotConnected;
  const isOtpResetLinkEnabled = !currentValidationErrors.userInfo;
  const isOtplessLoginLinkEnabled = !currentValidationErrors.userInfo;

  useEffect(() => {
    cloudWasNotConnected.current = cloudIsNotConnected;
  }, [cloudIsNotConnected]);

  useEffect(() => {
    const initialLoginData = getInitialLoginData(urlSearchQuery);
    updateLoginData(initialLoginData);
  }, [updateLoginData, urlSearchQuery]);

  const handleOtplessLoginLinkClick = () => {
    const validationErrors = getValidationErrors(loginData);
    setValidationErrors({ ...validationErrors, otp: false, any: false });

    if (isOtplessLoginLinkEnabled) {
      setLoginHasFailed(true);
      setShowRequestTokenForm(true);
      setIsOfflineLoginClicked(true);
    }
  };

  const handleResetOtpClick = () => {
    const validationErrors = getValidationErrors(loginData);
    setValidationErrors({ ...validationErrors, otp: false, any: false });
    
    if (isOtpResetLinkEnabled) {
      setLoginHasFailed(true);
      setShowRequestTokenForm(true);
      setIsOfflineLoginClicked(false);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDrag(true);
    } else if (e.type === "dragleave") {
      setDrag(false);
    }
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, [handleFileUpload]);
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  }, [handleFileUpload]);
  
  const onButtonClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const uploadedFileUrl = useMemo(() => uploadedFile ? URL.createObjectURL(uploadedFile) : null, [uploadedFile]) 
  
  const UploadSection = useCallback(() => {
    if (loginType === LoginType.Otp) return null;
    
    return (
      <Box sx={{ mt: 2, width: '460px', position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ color: '#65686B', fontSize: '14px', fontWeight: '400' }}>
              Upload ID
            </Typography>
            <Typography variant="body2" sx={{ color: '#65686B', fontSize: '12px', fontWeight: '400' }}>
              Drag & Drop files to attachments area
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Button
              startIcon={<FileUploadIcon />}
              onClick={onButtonClick}
              sx={{
                color: '#0C0C0C',
                fontWeight: '500',
                fontSize: '14px',
                fontFamily: 'Noto Sans, sans-serif',
                minWidth: '72px',
                textShadow: '0 0 5px rgba(0, 0, 0, 0.13)',
                padding: '8px 4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              Upload
            </Button>
            <Button
              startIcon={<CameraIcon />}
              onClick={onWebcamClick}
              sx={{
                color: '#0C0C0C',
                fontWeight: '500',
                fontSize: '14px',
                fontFamily: 'Noto Sans, sans-serif',
                minWidth: '72px',
                textShadow: '0 0 5px rgba(0, 0, 0, 0.13)',
                padding: '8px 4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              Camera
            </Button>
          </Box>
        </Box>
        {error ? <Box sx={{mb: 1, color: palette.error.main}}>{error}</Box> : null}
        <Box
          sx={{
            width: '460px',
            height: '76px',
            backgroundColor: '#F8F8F8',
            border: '1px solid #EFEFEF',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            marginBottom: '16px',
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={uploadedFile ? undefined : onButtonClick}
        >
          {uploadedFile ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                height: '100%',
                padding: '8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: '100px', height: '60px', overflow: 'hidden', borderRadius: '4px' }}>
                  <img
                    src={uploadedFileUrl || ""}
                    alt="Uploaded ID"
                    style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#0C0C0C', fontWeight: '500', fontSize: '14px', font: 'Noto Sans, sans-serif' }}
                  >
                    {uploadedFile.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: '#65686B', font: 'Noto Sans, sans-serif', fontWeight: '400', fontSize: '14px' }}
                  >
                    {new Date().toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadedFile(null);
                }}
                sx={{ position: 'absolute', right: '8px' }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <FileUploadIcon sx={{ color: '#65686B', fontSize: 24 }} />
              <Typography variant="body2" sx={{ color: '#65686B' }}>
                Drag and Drop files here or click to upload
              </Typography>
            </Box>
          )}
        </Box>
  
        <input type="file" ref={inputRef} onChange={handleChange} style={{ display: 'none' }} />
      </Box>
    );
  }, [loginType, onButtonClick, onWebcamClick, error, palette.error.main, handleDrag, handleDrop, uploadedFile, uploadedFileUrl, handleChange]);

  const otplessLoginLink = (
    <Box sx={{
      textAlign: "center",
      backgroundColor: palette.mode === "dark" ? "#293542" : "#D9EBFF",
      p: 2, 
      paddingX: 3, 
      mt: 2
    }}>
      If you can't connect to the internet, you can still log in without a
      One Time Password, but it may affect future identification
      <Box>
        <Box
          component="span"
          sx={{
            textDecoration: "underline",
            color: "#3AA4E6",
            cursor: "pointer",
            p: 1
          }}
          onClick={handleOtplessLoginLinkClick}
        >
          Login without One Time Password
        </Box>
      </Box>
    </Box>
  );

  const otpResetText = (
    <>
      Please enter the One Time Password that has been sent to your email, or click&nbsp;
      <Box
        component="span"
        sx={{
          textDecoration: "underline",
          color: "#3AA4E6",
          cursor: "pointer"
        }}
        onClick={handleResetOtpClick}
      >
        {t("button.requestOtp")}
      </Box>
      &nbsp;if you haven't received it yet
    </>
  );
  
  const otpOrUploadSection = isCloud ? (
    <Box sx={{ mb: 2.5 }}>
      <LoginOTPInput
        value={loginData.otp ?? ''}
        onValueChange={val => {
          setLoginHasFailed(false);
          updateLoginData({ otp: val });
        }}
        hasError={loginHasFailed}
        footerMessage={
          showOtplessLoginLink
            ? null
            : <Trans i18nKey="EnterOrRequestOtp">{otpResetText}</Trans>
        }
      />
      {showOtplessLoginLink && otplessLoginLink}
    </Box>
  ) : (
    <>
      <LoginOptionSwitcher loginType={loginType} setLoginType={setLoginType} />
      {loginType === LoginType.Upload ? <UploadSection /> : (
        <Box sx={{ mb: 2.5 }}>
          <LoginOTPInput
            value={loginData.otp ?? ''}
            onValueChange={val => {
              setLoginHasFailed(false);
              updateLoginData({ otp: val });
            }}
            hasError={loginHasFailed}
            footerMessage={
              showOtplessLoginLink
                ? null
                : <Trans i18nKey="EnterOrRequestOtp">{otpResetText}</Trans>
            }
          />
          {showOtplessLoginLink && otplessLoginLink}
        </Box>
      )}
    </>
  );

  const selectUserInput = (
    <SearchInput
      value={loginData.userType}
      onChange={event => updateLoginData({ userType: event.target.value as EdgeUserType })}
      options={[EdgeUserType.Customer, EdgeUserType.Surveyor]}
      label={t("form.userType.label")}
    />
  );

  const userForms = {
    [EdgeUserType.Barge]:
      <>
        <LoginFormField propertyName="email" />
        <LoginFormField propertyName="password" />
      </>,
    [EdgeUserType.Customer]:
      <>
        {selectUserInput}
        <LoginFormField propertyName="displayName" />
        <LoginFormField propertyName="vesselName" />
        <LoginFormField propertyName="rank" />
        <LoginFormField propertyName="email" />
        {otpOrUploadSection}
      </>,
    [EdgeUserType.Surveyor]: 
      <>
        {selectUserInput}
        <LoginFormField propertyName="displayName" />
        <LoginFormField propertyName="licenseNumber" />
        <LoginFormField propertyName="vesselName" />
        <LoginFormField propertyName="email" />
        {otpOrUploadSection}
      </>,
    [EdgeUserType.None]: null,
  };

  const failedLoginWarning = loginHasFailed ? (
    <AlertBanner
      sx={{ width: "460px" }}
      title="Sign-in failed"
      errorMessage={
        <Trans i18nKey="genericError">
          {isBargeLogin ? "Invalid email or password" : otpResetText}
        </Trans>
      }
    />
  ) : null;

  const isLoginButtonEnabled = loginType === LoginType.Otp
    ? !currentValidationErrors.any
    : !currentValidationErrors.userInfo && !!uploadedFile;

  const loginButton = (
    <Button
      disabled={isSubmitButtonLoading || !isLoginButtonEnabled}
      sx={{ 
        backgroundColor: "#1D82C0",
        '&:hover': {
          backgroundColor: '#1565A0',
        },
        width: '100%',
      }}
      onClick={() => doLogin(uploadedFile)}
      variant="contained"
    >
      {isSubmitButtonLoading ? (
        <CircularProgress size={18} />
      ) : (
        t(`button.${isBargeLogin ? 'user' : 'guest'}`)
      )}
    </Button>
  );

  const loginForm = (
    <LoginPageWrapper>
      {failedLoginWarning}
      <Box sx={{ width: "460px" }}>
        {userForms[userType]}
      </Box>
      {loginButton}
    </LoginPageWrapper>
  );

  const requestOTPForm = (
    <RequestOTP
      loginAction={doLogin}
      goBack={() => setShowRequestTokenForm(false)}
      isOfflineRequest={isOfflineLoginClicked}
    />
  );

  const form = showRequestTokenForm ? requestOTPForm : loginForm;

  return (
    <>
      {form}
      <Modal
        open={showCamera}
        onClose={handleCloseCamera}
        aria-labelledby="camera-modal"
        aria-describedby="camera-modal-description"
      >
        <Box 
          onClick={handleCloseCamera} 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <Box 
            onClick={(e: React.MouseEvent) => e.stopPropagation()} 
            sx={{
              width: '50%',
              height: '60%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <video ref={videoRef} style={{ width: '100%', height: 'auto', objectFit: 'cover' }} />
            <Button 
              onClick={handleTakePhoto} 
              sx={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CameraAltIcon fontSize="large" sx={{ color: '#65686B' }} />
            </Button>
              <Button
                  onClick={switchCamera}
                  sx={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '60%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'inherit',
                      '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                  }}
              >
                  <CameraswitchIcon fontSize="large" sx={{ color: palette.text.placeholder }} />
              </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

